import { ConfigService, Constants } from '@packages/core/config';
import { getLogger } from '@packages/core/logging';
import { doesContextSourceApplyToEvent } from './google-analytics.context-source';
import { GoogleAnalyticsEvents } from './google-analytics.events';
import { buildIdentifyPayload, hasSameTraits, GoogleAnalyticsUserTraits } from './google-analytics.identity';

import type {
    GoogleAnalyticsContextSource,
    GoogleAnalyticsContextSourceDisposer,
} from './google-analytics.context-source';
import type {
    GoogleAnalyticsEventName,
    GoogleAnalyticsEventProperties,
    GoogleAnalyticsTrackArgs,
} from './google-analytics.events';
import { GOOGLE_ANALYTICS_PAGE_SCOPED_PARAMS } from './google-analytics.params';
import { buildEventPayload } from './google-analytics.payload';

const logger = getLogger('google-analytics');

const GTM_SCRIPT_ID = 'gtm-script';
const GTM_BASE_URL = 'https://www.googletagmanager.com/gtm.js';
const GTM_COOKIES_WIN = 'x';

/**
 * Upper bound on the pre-boot event queue. Events can be tracked before the service boots (boot waits on auth
 * resolution and then on config), so those events are held rather than dropped; this cap keeps a service that never
 * boots (config failure, GTM disabled, a page that never signs in) from growing the queue without limit. Events
 * arriving once the queue is full are dropped with a warning rather than evicting older, already-queued ones.
 */
const MAX_QUEUED_EVENTS = 50;

declare global {
    interface Window {
        dataLayer?: Record<string, unknown>[];
    }
}

/**
 * Web-only Google Analytics 4 service that buffers events until auth resolves and GTM is ready.
 *
 * Boot sequence: the first `setUser`/`resetUser` call marks auth resolved, which triggers `start()`.
 * `start()` awaits config, pushes a one-time null identify baseline, then injects the GTM container script.
 * Events tracked before boot are queued (up to {@link MAX_QUEUED_EVENTS}) and flushed in order once the container is live.
 *
 * Context sources registered via `addContextSource` contribute ambient params that are merged under
 * each event's own properties at fire time, so call-site values always win over context defaults.
 *
 * All public methods swallow errors so analytics can never break the feature that triggered the call.
 */
export class GoogleAnalyticsService {
    private initPromise: Promise<void> | null = null;
    private isDisabled = false;
    private hasInjectedScript = false;

    private attachedTraits: GoogleAnalyticsUserTraits | null = null;
    private hasResolvedAuth = false;
    private hasBooted = false;
    private queuedEvents: Record<string, unknown>[] = [];
    private pendingIdentify: Record<string, unknown> | null = null;
    private previousEventParamKeys: readonly string[] = [];

    private contextSources = new Map<string, GoogleAnalyticsContextSource>();

    /**
     * The service is inert until auth resolves; nothing is read from config here because config may not be ready yet
     * at construction time, and reading it later keeps the constructor free of async work.
     *
     * @param configService - supplies the GTM enable flag, container id, and environment credentials at boot time
     */
    constructor(private readonly configService: ConfigService) {}

    /**
     * Identifies the signed-in user so subsequent hits can be attributed to them, and marks auth as resolved, which
     * is what boots the service. Traits equal to the currently attached ones are treated as a no-op so repeated calls
     * (re-renders, token refreshes) do not push redundant identify payloads.
     *
     * Failures are caught and warned rather than rethrown; analytics must never break the feature that called it.
     *
     * @param traits - the analytics-safe traits of the signed-in account
     */
    setUser(traits: GoogleAnalyticsUserTraits): void {
        try {
            if (hasSameTraits(this.attachedTraits, traits)) {
                this.markAuthResolved();
                return;
            }
            this.attachedTraits = traits;
            this.pushIdentify(buildIdentifyPayload(traits));
            this.markAuthResolved();
        } catch (error) {
            logger.warn('[google-analytics] setUser failed', error);
        }
    }

    /**
     * Clears any attached user and pushes a null identify payload so later hits are not attributed to the previous
     * account. Like `setUser`, it marks auth as resolved, so a visitor who is never signed in still boots the
     * service; with no user attached there is nothing to clear, so that case is a no-op beyond marking auth resolved.
     *
     * Failures are caught and warned rather than rethrown; analytics must never break the feature that called it.
     */
    resetUser(): void {
        try {
            if (this.attachedTraits === null) {
                this.markAuthResolved();
                return;
            }
            this.attachedTraits = null;
            this.pushIdentify(buildIdentifyPayload(null));
            this.markAuthResolved();
        } catch (error) {
            logger.warn('[google-analytics] resetUser failed', error);
        }
    }

    /** Mark auth as resolved on first identify call, signed in or not. Runs once. */
    private markAuthResolved(): void {
        if (this.hasResolvedAuth) {
            return;
        }
        this.hasResolvedAuth = true;
        void this.start();
    }

    /**
     * Records an analytics event. The payload is built here, at call time, from the supplied properties plus the
     * params contributed by whatever context sources are registered at this moment; when the service has not booted
     * yet, that finished payload is what gets queued. A context source registered afterwards therefore cannot
     * decorate an already-queued event, which is intentional: an event carries the context that was live when it
     * actually happened, not the context that existed whenever the queue drained.
     *
     * Failures are caught and warned rather than rethrown; analytics must never break the feature that called it.
     *
     * @template TEventName - the event being recorded, which determines whether properties are required
     * @param name - the GA4 event name
     * @param args - the event's properties, for events that declare any
     */
    track<TEventName extends GoogleAnalyticsEventName>(
        name: TEventName,
        ...args: GoogleAnalyticsTrackArgs<TEventName>
    ): void {
        try {
            this.pushEvent(name, args[0] as GoogleAnalyticsEventProperties | undefined);
        } catch (error) {
            logger.warn('[google-analytics] track failed', error);
        }
    }

    /**
     * Registers a named source of ambient params that are merged into events as they fire. The source's `getParams`
     * callback is evaluated at event-fire time, so it always reflects current state; nothing is snapshotted here.
     * Registering a second source under an existing name replaces the first one and warns.
     *
     * @param source - the context source to register
     *
     * @returns a disposer that unregisters this source, for callers that prefer cleanup over a name lookup
     *
     * @example
     * const dispose = googleAnalyticsService.addContextSource({
     *     name: 'vin-session-vehicle',
     *     getParams: () => (vinRecord ? { vin_session_vehicle_model: vinRecord.model } : undefined),
     *     filter: { except: VIN_SESSION_VEHICLE_CONTEXT_EXCLUDED_EVENTS },
     * });
     */
    addContextSource(source: GoogleAnalyticsContextSource): GoogleAnalyticsContextSourceDisposer {
        if (this.contextSources.has(source.name)) {
            logger.warn(`[google-analytics] context source replaced: ${source.name}`);
        }
        this.contextSources.delete(source.name);
        this.contextSources.set(source.name, source);

        return () => {
            // Only remove the source this disposer was handed out for; a later replacement owns the name now.
            if (this.contextSources.get(source.name) === source) {
                this.removeContextSource(source.name);
            }
        };
    }

    /**
     * Unregisters a context source by name. Registration is keyed by name so a caller that never kept the disposer
     * (or that registered from somewhere it cannot keep one) can still tear the source down.
     *
     * @param name - the name the source was registered under
     */
    removeContextSource(name: string): void {
        this.contextSources.delete(name);
    }

    /** Resolves config, injects GTM container script, and flushes pending events. */
    private async start(): Promise<void> {
        if (this.initPromise) {
            return this.initPromise;
        }

        this.initPromise = (async () => {
            try {
                await this.configService.ready();
            } catch (error) {
                this.isDisabled = true;
                return;
            }

            const isGtmEnabled = this.configService.getBoolean(Constants.Env.GtmEnabled, false);
            const containerId = this.configService.get(Constants.Env.GtmContainerId);

            if (!isGtmEnabled || !containerId || typeof window === 'undefined') {
                this.isDisabled = true;
                return;
            }
            this.flushPendingIdentify();
            this.injectContainerScript(containerId);
        })();
        await this.initPromise;
        this.hasBooted = true;
        this.flushQueue();
    }

    /**
     * Turns a tracked event into a dataLayer payload and either pushes it or queues it. Context params are merged in
     * before `buildEventPayload` runs, so params supplied by a source go through the same allow-list, sanitize and
     * truncate pipeline as ones passed at the call site; a payload the builder rejects (invalid event name) comes
     * back as `null` and is dropped here.
     *
     * @param name - the GA4 event name
     * @param properties - the properties supplied at the call site, if any
     */
    private pushEvent(name: GoogleAnalyticsEventName, properties: GoogleAnalyticsEventProperties | undefined): void {
        const payload = buildEventPayload(name, this.withContextParams(name, properties));
        if (payload === null) {
            return;
        }
        if (!this.hasBooted) {
            this.queueEvent(name, payload);
            return;
        }
        this.push(payload);
    }

    /**
     * Holds an already-built payload until the service boots, so events fired during startup are not lost. The queue
     * is capped by {@link MAX_QUEUED_EVENTS}; once it is full, further events are dropped with a warning naming the
     * event, since a queue that keeps growing means the service is never going to boot and flush it.
     *
     * @param name - the GA4 event name, used only for the warning when the event is dropped
     * @param payload - the finished payload to hold until boot
     */
    private queueEvent(name: GoogleAnalyticsEventName, payload: Record<string, unknown>): void {
        if (this.queuedEvents.length >= MAX_QUEUED_EVENTS) {
            logger.warn(`[google-analytics] queued events exceeded ${MAX_QUEUED_EVENTS}, dropping event: ${name}`);
            return;
        }
        this.queuedEvents.push(payload);
    }

    /**
     * Merges the params contributed by every applicable context source underneath the event's own properties. Runs
     * before `buildEventPayload` so source params go through the same allow-list, sanitize and truncate pipeline as
     * explicit ones.
     *
     * @param name - the event about to be pushed, used to evaluate each source's filter
     * @param properties - the properties supplied at the call site, which win over every source
     *
     * @returns the merged properties, or the original properties when no source contributed anything
     */
    private withContextParams(
        name: GoogleAnalyticsEventName,
        properties: GoogleAnalyticsEventProperties | undefined
    ): GoogleAnalyticsEventProperties | undefined {
        if (this.contextSources.size === 0) {
            return properties;
        }

        // Sources are applied in registration order, so a later source overwrites an earlier one's params.
        const merged: GoogleAnalyticsEventProperties = {};
        for (const source of this.contextSources.values()) {
            if (!doesContextSourceApplyToEvent(source, name)) {
                continue;
            }

            // A source that throws is skipped rather than allowed to take the whole event down with it.
            try {
                Object.assign(merged, source.getParams(name));
            } catch (error) {
                logger.warn(`[google-analytics] context source failed: ${source.name}`, error);
            }
        }

        if (Object.keys(merged).length === 0) {
            return properties;
        }

        return { ...merged, ...properties };
    }

    /**
     * Pushes an identify payload, or holds it until boot when the service is not up yet. Only the latest identify is
     * held: identify state is a snapshot of who is signed in right now, so an older one has no value once replaced.
     *
     * @param payload - the identify payload to push or hold
     */
    private pushIdentify(payload: Record<string, unknown>): void {
        if (this.hasBooted) {
            this.push(payload);
            return;
        }
        this.pendingIdentify = payload;
    }

    /**
     * Pushes the identify payload that was held during startup. This deliberately runs before the event queue is
     * flushed so the user is already attached to the dataLayer when the first queued hit lands; flushing in the other
     * order would send the earliest events as anonymous.
     */
    private flushPendingIdentify(): void {
        const pending = this.pendingIdentify;
        this.pendingIdentify = null;

        if (pending) {
            this.push(pending);
        }
    }

    /**
     * Drains the pre-boot queue in the order the events were tracked, so the sequence GA4 sees matches the sequence
     * the user produced. The queue is detached before pushing so anything tracked while draining starts a fresh
     * queue instead of being re-flushed or lost.
     */
    private flushQueue(): void {
        const queued = this.queuedEvents;
        this.queuedEvents = [];
        queued.forEach((payload) => this.push(payload));
    }

    /** The only write to the dataLayer. */
    private push(payload: Record<string, unknown>): void {
        if (this.isDisabled || typeof window === 'undefined') {
            return;
        }

        const finalPayload = this.clearParamsFromPreviousEvent(payload);
        (window.dataLayer ??= []).push(finalPayload);
        this.rememberEventParamKeys(payload);
    }

    /**
     * Adds explicit `undefined` entries for params the previous event set but this one does not. The dataLayer is
     * cumulative: GTM variables keep reading the last value pushed for a key, so without this a param would silently
     * ride along on later, unrelated events. Page-scoped params are additionally cleared on a page view, since they
     * describe the page being left rather than the one being entered. Only event pushes are treated this way;
     * identify pushes are meant to persist across hits, which is why they are passed through untouched.
     *
     * @param payload - the payload about to be written to the dataLayer
     *
     * @returns the payload, extended with the stale keys to clear; the payload's own values always win
     */
    private clearParamsFromPreviousEvent(payload: Record<string, unknown>): Record<string, unknown> {
        if (!isEventPush(payload)) {
            return payload;
        }

        const keysToClear =
            payload.event === GoogleAnalyticsEvents.PageView
                ? [...this.previousEventParamKeys, ...GOOGLE_ANALYTICS_PAGE_SCOPED_PARAMS]
                : this.previousEventParamKeys;

        if (keysToClear.length === 0) {
            return payload;
        }

        const paramsToClear: Record<string, unknown> = {};
        for (const previousKey of keysToClear) {
            if (!(previousKey in payload)) {
                paramsToClear[previousKey] = undefined;
            }
        }

        return { ...paramsToClear, ...payload };
    }

    /**
     * Records which param keys the event just pushed, so the next event knows what it has to clear. The event name
     * itself is excluded because it is set on every push, and page-scoped params are excluded because they are
     * expected to persist for the life of the page and are cleared only when a page view arrives. Identify pushes are
     * ignored for the same reason they are never cleared: their values are meant to stick.
     *
     * @param payload - the payload that was just written to the dataLayer
     */
    private rememberEventParamKeys(payload: Record<string, unknown>): void {
        if (!isEventPush(payload)) {
            return;
        }

        this.previousEventParamKeys = Object.keys(payload).filter(
            (key) => key !== 'event' && !GOOGLE_ANALYTICS_PAGE_SCOPED_PARAMS.includes(key)
        );
    }

    /**
     * Loads the GTM container for the configured id. The dataLayer is seeded with the environment name and GTM's
     * `gtm.start` marker before the script tag is added, because the container reads whatever is already in the
     * dataLayer as soon as it runs. Injection is guarded by both an instance flag and a lookup of the script id, so a
     * container that another bootstrap path already placed on the page is never loaded twice. `gtm_auth` and
     * `gtm_preview` are only sent when both are configured; they select a non-production container version, and one
     * without the other is not a usable pair. A load failure disables the service rather than leaving it pushing into
     * a dataLayer that no container will ever read.
     *
     * @param containerId - the GTM container id resolved from config
     */
    private injectContainerScript(containerId: string): void {
        if (this.hasInjectedScript || document.getElementById(GTM_SCRIPT_ID)) {
            return;
        }
        this.hasInjectedScript = true;

        (window.dataLayer ??= []).push({ environment: this.configService.debugEnvName });
        window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

        const queryParameters = new URLSearchParams({ id: containerId, gtm_cookies_win: GTM_COOKIES_WIN });
        const gtmAuth = this.configService.get(Constants.Env.GtmAuth, '');
        const gtmPreview = this.configService.get(Constants.Env.GtmPreview, '');
        if (gtmAuth && gtmPreview) {
            queryParameters.set('gtm_auth', gtmAuth);
            queryParameters.set('gtm_preview', gtmPreview);
        }

        const scriptElement = document.createElement('script');
        scriptElement.id = GTM_SCRIPT_ID;
        scriptElement.async = true;
        scriptElement.src = `${GTM_BASE_URL}?${queryParameters.toString()}`;
        scriptElement.onerror = () => {
            this.isDisabled = true;
        };
        document.head.appendChild(scriptElement);
    }
}

function isEventPush(payload: Record<string, unknown>): boolean {
    return typeof payload.event === 'string';
}
